import React, { useState } from 'react';
import { Modal, Button, Input, Form, message } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { X } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../contexts/AuthContext';
import styles from './ChangePasswordModal.module.css';

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      await authService.changePassword(values.currentPassword, values.newPassword);
      
      // Sau khi đổi mật khẩu thành công, tự động đăng nhập lại với password mới
      // để lấy token mới (vì token cũ đã bị invalidate)
      if (user?.email) {
        try {
          await login(user.email, values.newPassword);
          message.success('Đổi mật khẩu thành công! Đã tự động đăng nhập lại.');
        } catch (loginError: any) {
          // Nếu đăng nhập lại thất bại, vẫn hiển thị thành công nhưng yêu cầu đăng nhập lại
          message.warning('Đổi mật khẩu thành công! Vui lòng đăng nhập lại với mật khẩu mới.');
          await authService.logout();
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
        }
      } else {
        message.success('Đổi mật khẩu thành công! Vui lòng đăng nhập lại.');
        await authService.logout();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
      
      form.resetFields();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Vui lòng nhập mật khẩu mới'));
    }
    if (value.length < 8) {
      return Promise.reject(new Error('Mật khẩu phải có ít nhất 8 ký tự'));
    }
    if (!/(?=.*[a-z])/.test(value)) {
      return Promise.reject(new Error('Mật khẩu phải có ít nhất một chữ thường'));
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return Promise.reject(new Error('Mật khẩu phải có ít nhất một chữ hoa'));
    }
    if (!/(?=.*\d)/.test(value)) {
      return Promise.reject(new Error('Mật khẩu phải có ít nhất một số'));
    }
    if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(value)) {
      return Promise.reject(new Error('Mật khẩu phải có ít nhất một ký tự đặc biệt'));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Vui lòng xác nhận mật khẩu mới'));
    }
    if (value !== form.getFieldValue('newPassword')) {
      return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={500}
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Đổi mật khẩu</h2>
        
        <p className={styles.modalDescription}>
          Đảm bảo mật khẩu mới đủ mạnh, tối thiểu 8 ký tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt.
        </p>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={styles.form}
        >
          <Form.Item
            label="Mật khẩu hiện tại"
            name="currentPassword"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu hiện tại' }]}
          >
            <Input.Password
              placeholder="Nhập mật khẩu hiện tại"
              size="large"
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            rules={[{ validator: validatePassword }]}
          >
            <Input.Password
              placeholder="Nhập mật khẩu mới"
              size="large"
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            label="Xác nhận mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[{ validator: validateConfirmPassword }]}
          >
            <Input.Password
              placeholder="Nhập lại mật khẩu mới"
              size="large"
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <div className={styles.modalFooter}>
            <Button
              type="default"
              onClick={handleCancel}
              className={styles.cancelButton}
              size="large"
            >
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className={styles.confirmButton}
              size="large"
            >
              Xác nhận
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
